import { serverMessage } from '@ghp/api-client'
import type { IssuedCredentials } from '@ghp/domain'
import { Button, Field, Input, TextLink } from '@ghp/ui'
import { createRoute, Link, useNavigate, useRouterState } from '@tanstack/react-router'
import { KeyRound, LogIn } from 'lucide-react'
import { useId, useState } from 'react'

import { rootRoute } from '../../../app/routes/root'
import { AuthLayout } from '../components/auth-layout'
import s from './login.module.css'

/**
 * Le lien reçu par e-mail (D3). Le jeton est **derrière le `#`** : un fragment ne part ni au
 * serveur qui sert la SPA, ni dans un en-tête `Referer` — il ne traîne dans aucun journal.
 *
 * ⚠️ **Rien ne part au chargement de la page.** Les filtres de messagerie ouvrent les liens pour
 * les inspecter : un tirage au chargement consommerait le lien avant la personne. C'est le bouton
 * qui tire le mot de passe.
 */
export const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  component: ResetPasswordScreen,
})

function ResetPasswordScreen() {
  const { api } = resetPasswordRoute.useRouteContext()
  const token = useRouterState({ select: (state) => state.location.hash })
  const navigate = useNavigate()
  const failureId = useId()
  const [failure, setFailure] = useState<string | null>(null)
  const [pending, setPending] = useState(false)
  const [issued, setIssued] = useState<IssuedCredentials | null>(null)

  async function confirm(): Promise<void> {
    setFailure(null)
    setPending(true)
    try {
      setIssued(await api.auth.confirmPasswordReset({ token }))
    } catch (thrown) {
      setFailure(serverMessage(thrown))
    } finally {
      setPending(false)
    }
  }

  const newLink = (
    <TextLink render={<Link to="/forgot-password" />}>Demander un nouveau lien</TextLink>
  )

  if (token === '') {
    return (
      <AuthLayout title="Nouveau mot de passe">
        <p>Ce lien est incomplet : ouvrez-le depuis l’e-mail reçu, en entier.</p>
        {newLink}
      </AuthLayout>
    )
  }

  if (issued !== null) {
    return (
      <AuthLayout title="Nouveau mot de passe">
        <div className={s.form}>
          <Field label="Identifiant">
            {({ id }) => <Input id={id} readOnly value={issued.user.login} />}
          </Field>
          <Field label="Nouveau mot de passe">
            {({ id }) => <Input id={id} readOnly value={issued.password} />}
          </Field>
          <p>
            Notez-le maintenant : il ne s’affichera plus. Vos sessions ouvertes ont été fermées, sur
            tous vos appareils.
          </p>
          <Button
            tone="brand"
            fullWidth
            className={s.submit}
            onClick={() => void navigate({ to: '/login' })}
          >
            <LogIn size={18} aria-hidden />
            Se connecter
          </Button>
        </div>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout title="Nouveau mot de passe">
      <p>
        Ce lien tire un nouveau mot de passe pour votre compte, et ferme vos sessions ouvertes. Il
        ne sert qu’une fois.
      </p>
      <Button
        tone="brand"
        fullWidth
        className={s.submit}
        disabled={pending}
        aria-describedby={failure === null ? undefined : failureId}
        onClick={() => void confirm()}
      >
        <KeyRound size={18} aria-hidden />
        Obtenir mon nouveau mot de passe
      </Button>

      {failure !== null && (
        <>
          <p id={failureId} className={s.error} role="alert">
            {failure}
          </p>
          {newLink}
        </>
      )}
    </AuthLayout>
  )
}
